import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { GetOneUser, Task } from "../firebase-config";

const userCache = new Map<string, Task | null>();
const pendingRequests = new Map<string, Promise<Task | null>>();

const loadUserRecord = async (
  userId: string,
  forceRefresh = false,
): Promise<Task | null> => {
  if (!forceRefresh && userCache.has(userId)) {
    return userCache.get(userId) ?? null;
  }

  const pendingRequest = !forceRefresh ? pendingRequests.get(userId) : null;
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = GetOneUser(userId)
    .then((userRecord) => {
      userCache.set(userId, userRecord);
      pendingRequests.delete(userId);
      return userRecord;
    })
    .catch((error) => {
      pendingRequests.delete(userId);
      throw error;
    });

  pendingRequests.set(userId, request);
  return request;
};

export const invalidateCurrentUserData = (userId?: string) => {
  if (userId) {
    userCache.delete(userId);
    pendingRequests.delete(userId);
    return;
  }

  userCache.clear();
  pendingRequests.clear();
};

const useCurrentUserData = () => {
  const { user } = useAuth();
  const [userData, setUserData] = useState<Task | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshUserData = useCallback(async () => {
    if (!user?.uid) {
      setUserData(null);
      return null;
    }

    setIsLoading(true);

    try {
      const userRecord = await loadUserRecord(user.uid, true);
      setUserData(userRecord);
      return userRecord;
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setUserData(null);
      return;
    }

    const cachedUser = userCache.get(user.uid);
    if (cachedUser) {
      setUserData(cachedUser);
    }

    void refreshUserData();
  }, [refreshUserData, user?.uid]);

  return { userData, isLoading, refreshUserData };
};

export default useCurrentUserData;